import React, { useState } from 'react';
import {
    Box,
    Typography,
    Button,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    Tooltip,
    CircularProgress,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
} from '@mui/icons-material';
import { PageHeader } from '../../components/admin';
import type { Specializace } from '../../api/JsdhNotifikaceService/generated/models/Specializace';
import type { SpecializaceRequest } from '../../api/JsdhNotifikaceService/generated/models/SpecializaceRequest';
import {
    useAllSpecializaceQuery,
    useCreateSpecializaceMutation,
    useUpdateSpecializaceMutation,
    useDeleteSpecializaceMutation,
} from '../../api/JsdhNotifikaceService/service/SpecializaceMutations';

const SpecializacePage: React.FC = () => {
    const { data: specializace, isLoading, error } = useAllSpecializaceQuery();
    const createMutation = useCreateSpecializaceMutation();
    const updateMutation = useUpdateSpecializaceMutation();
    const deleteMutation = useDeleteSpecializaceMutation();

    // Dialog state
    const [openDialog, setOpenDialog] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [selectedSpec, setSelectedSpec] = useState<Specializace | null>(null);
    const [formData, setFormData] = useState<SpecializaceRequest>({ kod: '', nazev: '' });
    const [formError, setFormError] = useState<string | null>(null);

    const handleOpenCreate = () => {
        setSelectedSpec(null);
        setFormData({ kod: '', nazev: '' });
        setFormError(null);
        setOpenDialog(true);
    };

    const handleOpenEdit = (spec: Specializace) => {
        setSelectedSpec(spec);
        setFormData({ kod: spec.kod, nazev: spec.nazev });
        setFormError(null);
        setOpenDialog(true);
    };

    const handleOpenDelete = (spec: Specializace) => {
        setSelectedSpec(spec);
        setOpenDeleteDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedSpec(null);
        setFormData({ kod: '', nazev: '' });
        setFormError(null);
    };

    const handleSubmit = () => {
        setFormError(null);
        if (selectedSpec) {
            updateMutation.mutate(
                { id: selectedSpec.id, data: formData },
                {
                    onSuccess: () => handleCloseDialog(),
                    onError: (err: Error) => setFormError(err.message),
                }
            );
        } else {
            createMutation.mutate(formData, {
                onSuccess: () => handleCloseDialog(),
                onError: (err: Error) => setFormError(err.message),
            });
        }
    };

    const handleDelete = () => {
        if (selectedSpec) {
            deleteMutation.mutate(selectedSpec.id, {
                onSuccess: () => {
                    setOpenDeleteDialog(false);
                    setSelectedSpec(null);
                },
            });
        }
    };

    if (error) {
        return <Alert severity="error">Chyba při načítání specializací: {error.message}</Alert>;
    }

    return (
        <Box>
            <PageHeader
                title="Specializace"
                action={
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleOpenCreate}
                    >
                        Přidat specializaci
                    </Button>
                }
            />

            <Paper variant="outlined" sx={{ width: '100%', overflowX: 'auto' }}>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Kód</TableCell>
                                <TableCell>Název</TableCell>
                                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Vytvořeno</TableCell>
                                <TableCell align="center">Akce</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading ? (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : !specializace?.length ? (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                                        <Typography color="text.secondary">
                                            Žádné specializace
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                specializace.map((spec) => (
                                    <TableRow key={spec.id} hover>
                                        <TableCell>{spec.kod}</TableCell>
                                        <TableCell>{spec.nazev}</TableCell>
                                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                            {spec.createdAt
                                                ? new Date(spec.createdAt).toLocaleString('cs-CZ')
                                                : ''}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                                                <Tooltip title="Upravit">
                                                    <IconButton size="small" onClick={() => handleOpenEdit(spec)}>
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Smazat">
                                                    <IconButton size="small" color="error" onClick={() => handleOpenDelete(spec)}>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Create/Edit Dialog */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {selectedSpec ? 'Upravit specializaci' : 'Nová specializace'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        {formError && (
                            <Alert severity="error" onClose={() => setFormError(null)}>
                                {formError}
                            </Alert>
                        )}
                        <TextField
                            label="Kód"
                            value={formData.kod}
                            onChange={(e) => setFormData({ ...formData, kod: e.target.value.toUpperCase() })}
                            required
                            fullWidth
                            inputProps={{ maxLength: 20 }}
                            helperText="Krátký identifikátor (např. NDT, PILAR)"
                        />
                        <TextField
                            label="Název"
                            value={formData.nazev}
                            onChange={(e) => setFormData({ ...formData, nazev: e.target.value })}
                            required
                            fullWidth
                            inputProps={{ maxLength: 100 }}
                            helperText="Plný název specializace"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Zrušit</Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={
                            !formData.kod.trim() ||
                            !formData.nazev.trim() ||
                            createMutation.isPending ||
                            updateMutation.isPending
                        }
                    >
                        {(createMutation.isPending || updateMutation.isPending) && (
                            <CircularProgress size={20} sx={{ mr: 1 }} />
                        )}
                        {selectedSpec ? 'Uložit' : 'Vytvořit'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
                <DialogTitle>Smazat specializaci?</DialogTitle>
                <DialogContent>
                    <Typography>
                        Opravdu chcete smazat specializaci{' '}
                        <strong>{selectedSpec?.nazev} ({selectedSpec?.kod})</strong>?
                        Tato akce odebere specializaci všem uživatelům.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDeleteDialog(false)}>Zrušit</Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleDelete}
                        disabled={deleteMutation.isPending}
                    >
                        {deleteMutation.isPending && (
                            <CircularProgress size={20} sx={{ mr: 1 }} />
                        )}
                        Smazat
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SpecializacePage;
